import { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Button, Tabs, Tab, Spinner } from 'react-bootstrap';
import { Save } from 'lucide-react';
import type { ManualResponse, UpsertManualRequest } from '../../../infrastructure/AIAgentRegistryClient';
import { useAgentRegistry } from '../../../apps/hooks/agent/useAgentRegistry';
import { MarkdownEditor } from '../../../components/common/MarkdownEditor';

const STORAGE_KEY_PREFIX = 'manual-draft-';

interface UpsertManualModalProps {
  show: boolean;
  manual: ManualResponse | null;
  defaultCategory: string;
  onHide: () => void;
  onSubmit: (data: UpsertManualRequest) => void;
  isSubmitting: boolean;
}

export const UpsertManualModal = ({
  show,
  manual,
  defaultCategory,
  onHide,
  onSubmit,
  isSubmitting,
}: UpsertManualModalProps) => {
  const { categoriesQuery } = useAgentRegistry();
  const { data: categories = [], isLoading: isLoadingCategories } = categoriesQuery;
  const [activeTab, setActiveTab] = useState<'basic' | 'content'>('basic');
  const [name, setName] = useState('');
  const [category, setCategory] = useState(defaultCategory);
  const [description, setDescription] = useState('');
  const [bestPracticeIds, setBestPracticeIds] = useState('');
  const [manualMarkdown, setManualMarkdown] = useState('');
  const [hasDraft, setHasDraft] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isEdit = !!manual && !!manual.id;
  const storageKey = manual?.id ? `${STORAGE_KEY_PREFIX}${manual.id}` : null;

  useEffect(() => {
    if (manual) {
      setName(manual.name);
      setCategory(manual.category);
      setDescription(manual.description);
      setBestPracticeIds(manual.bestPracticeExecutionIds?.join(', ') || '');
      if (manual.id) {
        const draftKey = `${STORAGE_KEY_PREFIX}${manual.id}`;
        const savedDraft = localStorage.getItem(draftKey);
        if (savedDraft) {
          setManualMarkdown(savedDraft);
          setHasDraft(true);
        } else {
          setManualMarkdown(manual.manualMarkdown || '');
          setHasDraft(false);
        }
      } else {
        setManualMarkdown(manual.manualMarkdown || '');
        setHasDraft(false);
      }
    } else {
      setName('');
      setCategory(defaultCategory);
      setDescription('');
      setBestPracticeIds('');
      setManualMarkdown('');
      setHasDraft(false);
    }
    setActiveTab('basic');
    setErrors({});
  }, [manual, defaultCategory, show]);

  const saveDraft = useCallback((content: string) => {
    if (!storageKey) return;
    if (content.trim()) {
      localStorage.setItem(storageKey, content);
      setHasDraft(true);
    } else {
      localStorage.removeItem(storageKey);
      setHasDraft(false);
    }
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    if (!storageKey) return;
    localStorage.removeItem(storageKey);
    setHasDraft(false);
  }, [storageKey]);

  const discardDraft = useCallback(() => {
    if (!storageKey || !manual) return;
    localStorage.removeItem(storageKey);
    setManualMarkdown(manual.manualMarkdown || '');
    setHasDraft(false);
  }, [storageKey, manual]);

  const handleMarkdownChange = useCallback((value: string) => {
    setManualMarkdown(value);
    saveDraft(value);
  }, [saveDraft]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors['name'] = 'Name is required';
    if (!category.trim()) newErrors['category'] = 'Category is required';
    if (!description.trim()) newErrors['description'] = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const data: UpsertManualRequest = {
      name,
      category,
      description,
    };

    if (isEdit && manual) data.id = manual.id;
    if (bestPracticeIds.trim()) {
      data.bestPracticeExecutionIds = bestPracticeIds.split(',').map((id) => id.trim()).filter(Boolean);
    }
    if (manualMarkdown.trim()) data.manualMarkdown = manualMarkdown;

    clearDraft();
    onSubmit(data);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" dialogClassName="modal-80w">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit' : 'Create'} Manual</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k as 'basic' | 'content')} className="mb-3">
            <Tab eventKey="basic" title="Basic Info">
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., 주문 취소 매뉴얼"
              isInvalid={!!errors['name']}
            />
            <Form.Control.Feedback type="invalid">{errors['name']}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category *</Form.Label>
            {isLoadingCategories ? (
              <div className="d-flex align-items-center gap-2">
                <Spinner size="sm" />
                <span className="text-muted">Loading categories...</span>
              </div>
            ) : (
              <Form.Select value={category} onChange={(e) => setCategory(e.target.value)} isInvalid={!!errors['category']}>
                {categories.length === 0 ? (
                  <option>No categories available</option>
                ) : (
                  categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
                    </option>
                  ))
                )}
              </Form.Select>
            )}
            <Form.Control.Feedback type="invalid">{errors['category']}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this manual..."
              isInvalid={!!errors['description']}
            />
            <Form.Control.Feedback type="invalid">{errors['description']}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Best Practice Execution IDs (comma-separated, optional)</Form.Label>
            <Form.Control
              type="text"
              value={bestPracticeIds}
              onChange={(e) => setBestPracticeIds(e.target.value)}
              placeholder="uuid1, uuid2, uuid3"
            />
          </Form.Group>
            </Tab>
            <Tab eventKey="content" title={<span>Manual Content {hasDraft && <Save size={14} className="ms-1 text-warning" />}</span>}>
              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Manual Markdown</Form.Label>
                  {hasDraft && (
                    <div className="d-flex align-items-center gap-2">
                      <small className="text-warning">
                        <Save size={12} className="me-1" />Draft saved
                      </small>
                      <Button variant="outline-secondary" size="sm" onClick={discardDraft}>
                        Discard Draft
                      </Button>
                    </div>
                  )}
                </div>
                <Form.Text className="text-muted d-block mb-2">
                  Write the manual content using markdown format. Changes are auto-saved locally.
                </Form.Text>
                <MarkdownEditor
                  value={manualMarkdown}
                  onChange={handleMarkdownChange}
                  height={600}
                />
              </Form.Group>
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

